/**
 * Builds the vertical "Logistics log" steps for TaskReviewPage from task + dispute records.
 */
export function buildTaskLogisticsTimeline({
    task,
    latestSubmission,
    resolvedStatus,
    dispute = null,
}) {
    const status = String(resolvedStatus || '').toLowerCase();
    const postSubmitStatuses = new Set([
        'submitted',
        'approved',
        'released',
        'disputed',
        'refund_requested',
        'refunded',
        'rejected',
    ]);
    const progressDone = ['active', ...postSubmitStatuses].includes(status);
    const submittedDone =
        Boolean(latestSubmission) || postSubmitStatuses.has(status);

    const steps = [
        {
            id: 'assigned',
            label: 'Task assigned',
            date: task?.createdAt,
            icon: 'fa-solid fa-check',
            done: true,
        },
        {
            id: 'progress',
            label: 'Work in progress',
            date: task?.updatedAt || task?.createdAt,
            icon: 'fa-solid fa-code',
            done: progressDone,
        },
        {
            id: 'submitted',
            label: 'Work submitted',
            date: latestSubmission?.submittedAt,
            icon: 'fa-solid fa-arrow-up-from-bracket',
            done: submittedDone,
            current: status === 'submitted',
        },
    ];

    if (status === 'refund_requested') {
        steps.push({
            id: 'refund_requested',
            label: 'Refund requested',
            date: task?.updatedAt || task?.payment?.updatedAt,
            icon: 'fa-solid fa-rotate-left',
            done: true,
            current: true,
        });
    }

    const disputeStatus = String(dispute?.status || '').toLowerCase();
    const hasDisputeRecord = Boolean(dispute?.id);
    const onChainDisputed = Boolean(task?.onChain?.disputed);
    const showDisputeTrack =
        hasDisputeRecord || status === 'disputed' || onChainDisputed;

    if (showDisputeTrack) {
        steps.push({
            id: 'dispute_opened',
            label: 'Dispute opened',
            date: dispute?.createdAt || task?.updatedAt,
            icon: 'fa-solid fa-flag',
            done: true,
            current:
                status === 'disputed' &&
                !['in_review', 'resolved', 'closed'].includes(disputeStatus),
        });

        if (
            disputeStatus === 'in_review' ||
            disputeStatus === 'resolved' ||
            disputeStatus === 'closed'
        ) {
            steps.push({
                id: 'dispute_review',
                label: 'Dispute under review',
                date: dispute?.updatedAt || dispute?.createdAt,
                icon: 'fa-solid fa-magnifying-glass',
                done: disputeStatus !== 'in_review',
                current: disputeStatus === 'in_review',
            });
        }

        if (dispute?.resolvedAt || ['resolved', 'closed'].includes(disputeStatus)) {
            const outcome = dispute?.resolutionOutcome;
            let label = 'Dispute resolved';
            if (outcome === 'employee') {
                label = 'Dispute resolved · favor employee';
            } else if (outcome === 'company') {
                label = 'Dispute resolved · favor company';
            }
            steps.push({
                id: 'dispute_resolved',
                label,
                date: dispute?.resolvedAt,
                icon: 'fa-solid fa-gavel',
                done: true,
            });
        }
    }

    if (status === 'approved' || status === 'released') {
        steps.push({
            id: 'released',
            label: 'Payment released',
            date: task?.payment?.releasedAt || task?.updatedAt,
            icon: 'fa-solid fa-circle-check',
            done: true,
            current: status === 'approved',
        });
    }

    if (status === 'refunded') {
        steps.push({
            id: 'refunded',
            label: 'Funds refunded',
            date: task?.payment?.updatedAt || dispute?.resolvedAt || task?.updatedAt,
            icon: 'fa-solid fa-rotate-left',
            done: true,
            current: true,
        });
    }

    if (status === 'rejected') {
        steps.push({
            id: 'rejected',
            label: task?.escrowId ? 'Rework requested' : 'Submission rejected',
            date: task?.updatedAt,
            icon: task?.escrowId ? 'fa-solid fa-rotate' : 'fa-solid fa-circle-xmark',
            done: true,
            current: true,
        });
    }

    const hasCurrent = steps.some((s) => s.current);
    if (!hasCurrent) {
        for (let i = steps.length - 1; i >= 0; i -= 1) {
            if (steps[i].done) {
                steps[i] = { ...steps[i], current: true };
                break;
            }
        }
    }

    return steps;
}
